import React, { Component } from 'react';

import {
    availability, createActivity, addTable, removeTable, startActivity, releaseAll,
    ESTADO_ACTIVIDAD, mesasOcupadasDelError, textoDeAviso,
} from '../utils/activitiesApi';
import { hasInclusions } from '../utils/eventIncluded';
import { RESERVATION_STATUS } from '../utils/reservationsApi';
import { convertToMoney, formatYMD } from '../utils/formats';

// "Mesas del salón" — las mesas que una jornada del club le toma al salón, desde la pantalla de
// la jornada (FR-022…FR-024, FR-035…FR-037, FR-048, FR-051, FR-052, FR-058).
//
// Contrato de datos, que es de donde sale la mitad de los defectos posibles:
//
//   - Lo que se PINTA sale de `props.event.activity`, el objeto PLANO de `GET /manage-events/:id`
//     (`status`, `date`, `startHour`, `hasStarted`, `tableCount`, `expected`, `redeemed`,
//     `plannedTables[]`, `reservations[]`, `gamesByTable`) — todo AL TOPE, nunca
//     `activity.activity.*`. Sin actividad llega `null` y la card ofrece la primera mesa.
//   - Las cinco MUTACIONES de `/manage-actividades` devuelven la fila del modelo (snake_case) y
//     NO traen `tableCount` ni `hasStarted` ni las hijas. Por eso de una respuesta de mutación
//     acá se leen sólo `warnings` / `released` / `kept` / `capacity`, y el estado se vuelve a
//     pedir con `props.onRefresh()`. Pintar con lo que devuelve `addTable` deja la card vacía
//     después de cada acción y sin ningún error visible.
//   - `attendees` viaja HERMANO de `event` en la respuesta del detalle (`data.attendees`), no
//     adentro: la página lo baja y lo pasa por prop.
//
// TC-002: acá no se calcula ninguna fecha de jornada. `hasStarted`, `date` y `businessDate` los
// dice el backend. Lo único que el navegador arma es el par (date, startHour) con el que se le
// PIDE la disponibilidad cuando la actividad todavía no existe — ver `slot()`.
class EventSalonTablesComponent extends Component {

    constructor(props) {
        super(props);
        this.state = {
            trabajando: false,
            error: null,
            // Mesas que el backend rechazó con 7304, con su motivo: la lista viaja en
            // `err.body.data` (un array) y no en el mensaje.
            ocupadas: [],
            aviso: null,
            warnings: [],
            // Mesas que "Liberar todas" NO liberó porque tienen consumo impago.
            conSaldo: [],
            sumando: false,
            disponibilidad: null,
            cargandoDisponibilidad: false,
            tableId: '',
            capacityDelta: '',
        };
    }

    /**
     * La actividad de la jornada, o `null`.
     *
     * El `&& act.status` no es paranoia: es el binding del contrato de TD-21. Si algún día el
     * backend envolviera el adjunto (`event.activity = { activity: {...} }`), todos los campos
     * que lee la card quedarían `undefined` y la pantalla mostraría una jornada con estado
     * vacío, fecha "—" y ningún botón, sin un solo error. Exigir `status` —que el objeto plano
     * SIEMPRE trae— hace que ese caso caiga limpio en "todavía no tomó mesas".
     */
    actividad = () => {
        const act = (this.props.event && this.props.event.activity) || null;
        return act && act.status ? act : null;
    }

    anotados = () => {
        const att = this.props.attendees;
        if (att) return { confirmed: Number(att.confirmed) || 0, pending: Number(att.pending) || 0, redeemed: Number(att.redeemed) || 0 };
        const act = this.actividad();
        // `expected` y `redeemed` de la actividad salen del MISMO cálculo que `attendees`
        // (el detalle se los inyecta), así que sirven de respaldo; `pending` sólo vive en
        // `attendees`.
        return { confirmed: act ? Number(act.expected) || 0 : 0, pending: 0, redeemed: act ? Number(act.redeemed) || 0 : 0 };
    }

    /**
     * El slot con el que se PIDE la disponibilidad (FR-051). No es la jornada: es el argumento
     * de una consulta.
     *
     * Con actividad creada, los dos valores vienen del backend (`activity.date` / `.startHour`)
     * y son exactos. Sin actividad todavía no hay a quién preguntarle —`/availability` no toma
     * `eventId`— así que se leen los DÍGITOS de `event.starts_at` (nunca `new Date`, TC-002).
     * Coinciden con la jornada salvo en el caso nocturno: un evento de las 00:30 pertenece a la
     * jornada de AYER, y eso lo decide el `closeHour` del día anterior, que el navegador no
     * tiene. La consecuencia está acotada al selector: la reserva real la hace el backend con
     * SU slot y, si alguna mesa choca, rechaza con 7304 y la lista de mesas ocupadas.
     */
    slot = () => {
        const act = this.actividad();
        if (act) return { date: act.date, startHour: Number(act.startHour) };
        const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):/.exec(String((this.props.event && this.props.event.starts_at) || ''));
        return m ? { date: m[1], startHour: Number(m[2]) } : {};
    }

    limpiar = () => this.setState({ error: null, ocupadas: [], aviso: null, warnings: [], conSaldo: [] });

    fallo = (e) => {
        this.setState({
            trabajando: false,
            error: e.message || 'No se pudo completar la operación',
            ocupadas: mesasOcupadasDelError(e),
        });
    }

    // Toda mutación termina acá: se guarda lo que la respuesta SÍ trae y se vuelve a pedir el
    // estado, que es lo único que puede pintar la card.
    refrescar = (extra = {}) => {
        this.setState({ trabajando: false, sumando: false, tableId: '', capacityDelta: '', disponibilidad: null, ...extra });
        if (this.props.onRefresh) this.props.onRefresh();
    }

    // ── Sumar mesa (FR-022, FR-046, FR-051) ───────────────────────────────────────────────
    abrirSumar = () => {
        this.limpiar();
        this.setState({ sumando: true, cargandoDisponibilidad: true, disponibilidad: null, tableId: '', capacityDelta: '' });
        const { date, startHour } = this.slot();
        availability({ date, startHour })
            .then((data) => this.setState({ disponibilidad: data, cargandoDisponibilidad: false }))
            .catch((e) => this.setState({ cargandoDisponibilidad: false, error: e.message }));
    }

    mesaElegida = () => {
        const disp = this.state.disponibilidad;
        if (!disp || !this.state.tableId) return null;
        return (disp.tables || []).find((t) => String(t.id) === String(this.state.tableId)) || null;
    }

    // Cuánto sube el cupo cuando se suma la mesa con el atajo de FR-023: por defecto, lo que
    // entra en la mesa elegida. Se puede editar; nunca baja (el backend ignora un delta <= 0).
    deltaPropuesto = () => {
        const escrito = String(this.state.capacityDelta).trim();
        if (escrito !== '') return parseInt(escrito, 10);
        const mesa = this.mesaElegida();
        return mesa ? Number(mesa.capacity) || 0 : 0;
    }

    sumarMesa = (conCupo) => {
        const mesa = this.mesaElegida();
        if (!mesa) { this.setState({ error: 'Elegí la mesa que se suma a la jornada.' }); return; }
        const delta = conCupo ? this.deltaPropuesto() : null;
        if (conCupo && (!Number.isInteger(delta) || delta <= 0)) {
            this.setState({ error: 'El cupo se amplía en un número entero de lugares.' });
            return;
        }
        this.limpiar();
        this.setState({ trabajando: true });
        const act = this.actividad();
        const llamada = act
            ? addTable(act.id, { tableId: mesa.id, capacityDelta: delta || undefined })
            : createActivity({ eventId: this.props.event.id, tableIds: [mesa.id], capacityDelta: delta || undefined });
        llamada
            .then((data) => {
                const cupo = data && data.capacity;
                this.refrescar({
                    aviso: `Se sumó ${mesa.label} a la jornada.${cupo != null ? ` Cupo del evento: ${cupo}.` : ''}`,
                    warnings: (data && data.warnings) || [],
                });
            })
            .catch(this.fallo);
    }

    // ── Quitar (FR-047) ───────────────────────────────────────────────────────────────────
    quitarMesa = (mesa) => {
        const act = this.actividad();
        if (!act) return;
        if (!window.confirm(`¿Quitar ${mesa.label} de la jornada? Se cancela la reserva de esa mesa; el resto del plan queda igual.`)) return;
        this.limpiar();
        this.setState({ trabajando: true });
        removeTable(act.id, mesa.tableId)
            .then(() => this.refrescar({ aviso: `${mesa.label} salió de la jornada.` }))
            .catch(this.fallo);
    }

    // ── Sentar las mesas (FR-036, FR-068) ─────────────────────────────────────────────────
    //
    // SIN `headcount`: así el backend reparte los `redeemed` del evento entre las mesas. Mandar
    // 0 dejaría la actividad sentada con cero personas en mesa.
    sentar = () => {
        const act = this.actividad();
        if (!act) return;
        this.limpiar();
        this.setState({ trabajando: true });
        startActivity(act.id)
            .then((data) => this.refrescar({
                aviso: `Se sentaron ${(data && data.seated) || 0} mesas.`,
                warnings: (data && data.warnings) || [],
            }))
            .catch(this.fallo);
    }

    // ── Sentar a todos ────────────────────────────────────────────────────────────────────
    //
    // Sólo en un evento que declara qué incluye la entrada: ya está todo pago, así que se da por
    // llegado a todo el mundo. El backend sienta las mesas, reparte parejo a los anotados y marca
    // sus entradas como ingresadas.
    sentarATodos = () => {
        const act = this.actividad();
        if (!act) return;
        this.limpiar();
        this.setState({ trabajando: true });
        startActivity(act.id, { everyone: true })
            .then((data) => this.refrescar({
                aviso: `Se sentaron ${(data && data.seated) || 0} mesas con todos los anotados.`,
                warnings: (data && data.warnings) || [],
            }))
            .catch(this.fallo);
    }

    // ── Liberar todas (FR-037) ────────────────────────────────────────────────────────────
    liberarTodas = () => {
        const act = this.actividad();
        if (!act) return;
        if (!window.confirm('¿Liberar todas las mesas de la jornada? Las que tengan consumo impago quedan abiertas para cobrarlas desde el salón.')) return;
        this.limpiar();
        this.setState({ trabajando: true });
        releaseAll(act.id)
            .then((data) => {
                const liberadas = (data && data.released) || [];
                const conSaldo = (data && data.kept) || [];
                this.refrescar({
                    aviso: `Se liberaron ${liberadas.length} ${liberadas.length === 1 ? 'mesa' : 'mesas'}.`,
                    conSaldo,
                });
            })
            .catch(this.fallo);
    }

    // ── Render ────────────────────────────────────────────────────────────────────────────
    renderAvisos = () => {
        const { error, ocupadas, aviso, warnings, conSaldo } = this.state;
        return (
            <>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                        {ocupadas.length > 0 && (
                            <ul className="mb-0 mt-2">
                                {ocupadas.map((m, i) => (
                                    <li key={`${m.label}-${i}`}>
                                        <b>{m.label}</b>: {m.reason}
                                        {m.reason === 'Mesa bloqueada' && ' — quitá el bloqueo desde Bloqueos y volvé a publicar.'}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {aviso && <div className="alert alert-success" role="alert">{aviso}</div>}
                {warnings.length > 0 && (
                    <div className="alert alert-warning" role="alert">
                        <b>Avisos del salón:</b>
                        <ul className="mb-0 mt-1">
                            {warnings.map((w, i) => (
                                <li key={i}>{textoDeAviso(w)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {conSaldo.length > 0 && (
                    <div className="alert alert-warning" role="alert">
                        <b>Quedaron con la cuenta abierta</b> (se cobran desde el salón):
                        <ul className="mb-0 mt-1">
                            {conSaldo.map((m) => (
                                <li key={m.id}>{m.label}: $ {convertToMoney(Number(m.balance) || 0)} en {m.orders} {Number(m.orders) === 1 ? 'comanda' : 'comandas'}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </>
        );
    }

    // cupo · anotados · ingresaron · en mesa, lado a lado y SIN reconciliarlos (FR-035): son
    // cuatro números de cosas distintas y forzarlos a cerrar escondería el problema real.
    renderNumeros = () => {
        const event = this.props.event;
        const act = this.actividad();
        const att = this.anotados();
        const capacity = event.capacity == null ? null : Number(event.capacity);
        const sold = Number(event.sold) || 0;
        const vivas = act ? (act.reservations || []).filter((r) => r.status === 'active' || r.status === 'seated') : [];
        const enMesa = vivas.reduce((acc, r) => acc + (Number(r.playerCount) || 0), 0);
        const desglose = vivas.length ? vivas.map((r) => Number(r.playerCount) || 0).join('+') : null;
        const dato = (label, valor, extra) => (
            <div className="mr-4 mb-2">
                <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{valor}{extra}</div>
            </div>
        );
        return (
            <div className="d-flex flex-wrap align-items-start">
                {dato('Cupo', capacity == null ? '—' : `${sold}/${capacity}`, capacity == null ? <small className="text-muted"> sin cupo</small> : null)}
                {dato('Anotados', att.confirmed, att.pending > 0 ? <small className="text-muted"> (+{att.pending} pend.)</small> : null)}
                {dato('Ingresaron', att.redeemed, null)}
                {dato('En mesa', enMesa, desglose ? <small className="text-muted"> ({desglose})</small> : null)}
            </div>
        );
    }

    renderCupo = () => {
        const event = this.props.event;
        const act = this.actividad();
        const att = this.anotados();
        const capacity = event.capacity == null ? null : Number(event.capacity);
        const sold = Number(event.sold) || 0;
        const mesas = act ? (act.plannedTables || []) : [];
        const capacidadMesas = mesas.reduce((acc, m) => acc + (Number(m.capacity) || 0), 0);
        const lleno = capacity != null && sold >= capacity;
        return (
            <>
                {/* FR-022: los dos números se muestran, no se cruzan. Que el cupo del evento sea
                    mayor que la capacidad de las mesas es una decisión del encargado (hay gente
                    que viene a mirar), no un error que la pantalla deba impedir. */}
                <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                    cupo {capacity == null ? '—' : capacity} · {mesas.length} {mesas.length === 1 ? 'mesa' : 'mesas'}, capacidad {capacidadMesas}
                </p>
                {lleno && (
                    <div className="alert alert-info py-2" role="alert" style={{ fontSize: 13 }}>
                        <b>Cupo completo</b> · {sold} vendidas ({att.confirmed} confirmadas + {att.pending} pendientes) de {capacity}.
                        {' '}Para dejar entrar más gente, sumá una mesa y ampliá el cupo en el mismo paso.
                    </div>
                )}
                {/* FR-024: sin cupo numérico no hay señal de lleno que dar. */}
                {capacity == null && (
                    <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Este evento no tiene cupo numérico, así que nunca va a avisar "cupo completo": conviene fijarle uno, aunque sea holgado.
                    </p>
                )}
            </>
        );
    }

    renderMesa = (mesa) => {
        const act = this.actividad();
        // Una mesa puede tener MÁS DE UNA hija en la lista: la de anoche liberada (`completed`)
        // y la de hoy. Manda la viva; sin ninguna viva se muestra la última que hubo, que es lo
        // que explica por qué la mesa figura así.
        const suyas = (act.reservations || []).filter((r) => Number(r.tableId) === Number(mesa.tableId));
        const hija = suyas.find((r) => r.status === 'active' || r.status === 'seated') || suyas[suyas.length - 1] || null;
        const estado = hija ? (RESERVATION_STATUS[hija.status] || { label: hija.status, badge: 'badge-light' }) : null;
        const juegos = (act.gamesByTable || {})[String(mesa.tableId)] || [];
        const terminada = act.status === 'done' || act.status === 'cancelled';
        return (
            <div key={mesa.tableId} className="d-flex justify-content-between align-items-start py-2" style={{ borderBottom: '0.5px solid #eee' }}>
                <div>
                    <b>{mesa.label}</b>
                    <small className="text-muted"> · {mesa.capacity == null ? '—' : mesa.capacity} lugares</small>
                    {estado && <span className={`badge ${estado.badge} ml-2`}>{estado.label}</span>}
                    {hija && Number(hija.playerCount) > 0 && (
                        <small className="text-muted"> · {hija.playerCount} {Number(hija.playerCount) === 1 ? 'persona' : 'personas'}</small>
                    )}
                    {!hija && <small className="text-muted ml-2">planificada (se reserva al publicar)</small>}
                    {/* FR-052: los juegos los carga el salón desde el panel de la mesa; acá se
                        leen, que es para lo que sirve tenerlos juntos en la jornada. */}
                    {juegos.length > 0 && (
                        <div className="text-muted" style={{ fontSize: 12 }}>Juegos: {juegos.map((g) => g.gameName).join(', ')}</div>
                    )}
                </div>
                {!terminada && (
                    <button type="button" className="btn btn-sm btn-outline-danger text-nowrap"
                        disabled={this.state.trabajando} onClick={() => this.quitarMesa(mesa)}>
                        Quitar
                    </button>
                )}
            </div>
        );
    }

    renderSumar = () => {
        const disp = this.state.disponibilidad;
        const event = this.props.event;
        const capacity = event.capacity == null ? null : Number(event.capacity);
        const lleno = capacity != null && (Number(event.sold) || 0) >= capacity;
        const elegida = this.mesaElegida();
        const { date } = this.slot();
        const act = this.actividad();
        const enElPlan = new Set((act ? (act.plannedTables || []) : []).map((m) => Number(m.tableId)));
        if (!this.state.sumando) {
            return (
                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.trabajando} onClick={this.abrirSumar}>
                    Sumar mesa
                </button>
            );
        }
        return (
            <div className="border rounded p-3 mt-2">
                {this.state.cargandoDisponibilidad && <p className="text-muted mb-0">Buscando mesas libres…</p>}
                {disp && (
                    <>
                        <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                            Disponibilidad del {formatYMD(date || disp.businessDate)} desde las {disp.fromHour}:00.
                            {/* La jornada del salón EN CURSO la dice el backend; se muestra al
                                lado para que se vea cuándo la jornada que se está armando es
                                otro día. */}
                            {disp.businessDate && String(disp.businessDate) !== String(date) && (
                                <span> La jornada del salón de hoy es la del {formatYMD(disp.businessDate)}.</span>
                            )}
                        </p>
                        {/* FR-048: el día cerrado no impide la actividad — el evento es el motivo
                            de abrir. Se avisa y nada más. */}
                        {disp.closedDay && (
                            <div className="alert alert-warning py-2" role="alert" style={{ fontSize: 13 }}>
                                El salón figura cerrado ese día. La jornada se puede armar igual.
                            </div>
                        )}
                        <div className="form-group mb-2">
                            <select className="form-control" value={this.state.tableId}
                                onChange={(e) => this.setState({ tableId: e.target.value })}>
                                <option value="">Elegí la mesa…</option>
                                {/* Las mesas que ya están en el plan no se ofrecen: sumarlas otra
                                    vez no tiene sentido, y para el salón figuran "libres" mientras
                                    la jornada es un borrador. */}
                                {(disp.tables || []).filter((t) => !enElPlan.has(Number(t.id))).map((t) => (
                                    <option key={t.id} value={t.id} disabled={t.state !== 'free'}>
                                        {t.label} ({t.capacity} lugares{t.isVip ? ', VIP' : ''})
                                        {t.state !== 'free' ? ` — ${t.reason || 'No disponible'}` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {elegida && elegida.nextReservation && (
                            <p className="text-warning mb-2" style={{ fontSize: 13 }}>
                                Ojo: {elegida.label} tiene una reserva a las {elegida.nextReservation.startHour}:00
                                {elegida.nextReservation.name ? ` (${elegida.nextReservation.name})` : ''}.
                            </p>
                        )}
                        {lleno && (
                            <div className="form-group mb-2" style={{ maxWidth: 220 }}>
                                <label style={{ fontSize: 13 }}>Ampliar el cupo en</label>
                                <input type="number" className="form-control form-control-sm" min="1"
                                    placeholder={elegida ? String(Number(elegida.capacity) || 0) : 'lugares'}
                                    value={this.state.capacityDelta}
                                    onChange={(e) => this.setState({ capacityDelta: e.target.value })} />
                                <small className="text-muted">Vacío = lo que entra en la mesa elegida.</small>
                            </div>
                        )}
                        <button type="button" className="btn btn-sm btn-accent mr-2"
                            disabled={this.state.trabajando || !this.state.tableId} onClick={() => this.sumarMesa(false)}>
                            Sumar mesa
                        </button>
                        {/* FR-023: la mesa y el cupo suben en la MISMA transacción. Si la mesa
                            choca (7304), el cupo tampoco se mueve. */}
                        {lleno && (
                            <button type="button" className="btn btn-sm btn-accent mr-2"
                                disabled={this.state.trabajando || !this.state.tableId} onClick={() => this.sumarMesa(true)}>
                                Sumar mesa y ampliar cupo{elegida ? ` (+${this.deltaPropuesto()})` : ''}
                            </button>
                        )}
                    </>
                )}
                <button type="button" className="btn btn-sm btn-white" disabled={this.state.trabajando}
                    onClick={() => this.setState({ sumando: false, disponibilidad: null, tableId: '', capacityDelta: '' })}>
                    Cancelar
                </button>
            </div>
        );
    }

    renderAcciones = () => {
        const act = this.actividad();
        if (!act) return null;
        const hijas = act.reservations || [];
        // FR-036: fallback del check-in automático. Con la actividad ya sentada sigue estando
        // para las mesas que quedaron `active` porque estaban tomadas (FR-060).
        const puedeSentar = (act.status === 'reserved' && Number(act.redeemed) > 0)
            || (act.status === 'seated' && hijas.some((r) => r.status === 'active'));
        // FR-037: mismo criterio que el salón. `hasStarted` lo dice el backend.
        const puedeLiberar = act.status === 'seated' || !!act.hasStarted;
        // "Sentar a todos (N)": en un evento con inclusiones, con las mesas reservadas y sin
        // sentar, haya o no ingresos. Ahí reemplaza a "Sentar las mesas".
        const anotados = Number(act.expected) || 0;
        const puedeSentarATodos = hasInclusions(this.props.event.included) && act.status === 'reserved' && anotados > 0;
        if (!puedeSentar && !puedeLiberar && !puedeSentarATodos) return null;
        return (
            <div className="mt-3">
                {puedeSentarATodos && (
                    <button type="button" className="btn btn-sm btn-accent mr-2" disabled={this.state.trabajando} onClick={this.sentarATodos}>
                        Sentar a todos ({anotados})
                    </button>
                )}
                {puedeSentar && !puedeSentarATodos && (
                    <button type="button" className="btn btn-sm btn-accent mr-2" disabled={this.state.trabajando} onClick={this.sentar}>
                        Sentar las mesas
                    </button>
                )}
                {puedeLiberar && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={this.state.trabajando} onClick={this.liberarTodas}>
                        Liberar todas
                    </button>
                )}
            </div>
        );
    }

    render() {
        const event = this.props.event;
        if (!event) return null;
        const act = this.actividad();
        const estado = act ? (ESTADO_ACTIVIDAD[act.status] || { label: act.status, badge: 'badge-light' }) : null;
        const mesas = act ? (act.plannedTables || []) : [];
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                    <h6 className="m-0 mr-auto">Mesas del salón</h6>
                    {estado && <span className={`badge ${estado.badge}`}>{estado.label}</span>}
                    {act && (
                        <small className="text-muted">
                            Jornada del {formatYMD(act.date)} a las {act.startHour}:00
                        </small>
                    )}
                </div>
                <div className="card-body">
                    {this.renderAvisos()}
                    {this.renderNumeros()}
                    {this.renderCupo()}
                    {mesas.length > 0
                        ? <div className="mb-3">{mesas.map((m) => this.renderMesa(m))}</div>
                        : (
                            <p className="text-muted mb-3">
                                Esta jornada todavía no tomó mesas del salón. La primera que sumes crea el plan;
                                {' '}con el evento publicado, las mesas se reservan en el acto.
                            </p>
                        )}
                    {this.renderSumar()}
                    {this.renderAcciones()}
                </div>
            </div>
        );
    }
}

export default EventSalonTablesComponent;
